import { NavigationProp, ParamListBase, RouteProp, useIsFocused, useNavigation } from "@react-navigation/native";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, FlatList, Pressable, ScrollView, StyleProp, TextStyle } from "react-native";
import "../util/datePicker.css";
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { kRoute } from "src/kRoute";
import { addShipping, declineOrder, updateOrder, updateProductOrders } from "src/redux/orders/actions";
import { setBackButtonRoute } from "src/redux/navigation/thunkActions";
import { getItemPrice } from "src/util/functions";
import { kColors } from "../kColors";
import {
  displayProperConnectionSource,
  formatDate,
  formatMoney,
  formatPaymentMethod,
  formatPhoneNumber,
  formatShortAddress,
} from "../util/gFormatting";
import { kStyleConst } from "src/kStyle";
import { PdArrowUpRight, PdCheck, PdPaper, PdPaperAlt, PdPapers, PdReorder, PdTrash } from "src/components/Icons";
import { PdBadge, PdBadgeLabel, PdStack, PdText } from "src/components/layout";
import { getThemeColorFromOrderStatus } from "src/util/ui";
import { getDefaultAddress } from "src/util/user";
import { kFonts } from "src/kFonts";
import { useModal } from "src/providers/OverlayProvider";
import { AlertModal, tAlertModalProps } from "src/components/Modals/AlertModal";
import { getApiError, getOrderInvoice, getOrderPackingSlip, isApiError, postReorder } from "src/util/apiNew";
import { getProductInfoFromCartProduct } from "src/util/cart";
import {
  PdDivider,
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/layout/table";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { TesterBadge } from "src/components/ui";
import { useIsMobile, useUserProfile } from "src/hooks/ui";
import { PdIconButton } from "src/components/forms/icon-button";
import { PdDateInput, PdInput, PdInputNumber } from "src/components/forms/input";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useMutation } from "src/hooks/api";
import { useAppDispatch } from "src/redux";
import { PdFormLabel } from "src/components/form-control";
import { openUrlInNewTab } from "src/util/linking";
import { tPressableState } from "src/components/types";
import { useAssignRepOrderModal, useSplitOrderModal } from "src/hooks/modals";
import { tCartProduct, tOrder, tOrderStatus } from "tmt_api/src/types/types";
import * as orderDb from "src/db/order.db";

type tpViewOrderParamList = {
  order: {
    id: string;
  };
};

type tTableProduct = {
  id: string;
  productId: string;
  brandId: string;
  brandName: string;
  name: string;
  sku: string;
  quantity: number;
  originalQuantity?: number;
  caseMinimum: number;
  costPerUnit: number;
  total: number;
  isTester: boolean;
};

const roundInSteps = (value: number, step: number): number => {
  return Math.ceil(value / step) * step;
};

const mapTableProduct = (product: tCartProduct): tTableProduct => {
  const { caseMinimum, isTester } = getProductInfoFromCartProduct(product);
  return {
    id: product.isTester ? product.tester?.sku ?? `${product.id ?? ""}` : product.actualSKU,
    productId: product.productId,
    brandId: product.brandUid,
    brandName: product.brandName,
    name: product.name,
    sku: product.actualSKU,
    quantity: product.quantity,
    originalQuantity: product.originalQuantity,
    caseMinimum,
    costPerUnit: !product.quantity ? 0 : getItemPrice(product) / product.quantity,
    total: product.totalPrice,
    isTester,
  };
};

const QuantityControlOrder: FC<
  Omit<tTableProduct, "id" | "name" | "brandName" | "costPerUnit" | "total" | "isTester"> & {
    orderId: string;
    orderStatus: tOrderStatus;
  }
> = ({ productId, brandId, sku, quantity, originalQuantity, caseMinimum, orderId, orderStatus }) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(setBackButtonRoute(kRoute.P03));
  }, [dispatch]);
  const { isBrand } = useUserProfile();
  const [sQuantity, setQuantity] = useState<number>(quantity);
  const rCanEdit = orderStatus === "Pending" && isBrand;
  const rCanAdd = rCanEdit && (originalQuantity ? quantity < originalQuantity : true);

  const addQuantity = () => {
    if (rCanAdd) {
      const newQuantity = roundInSteps(quantity + caseMinimum, caseMinimum);
      dispatch(
        updateProductOrders(orderId, {
          brandId,
          productId,
          originalQuantity,
          quantity: newQuantity,
          sku,
          type: "PRODUCT",
        }),
      );
    }
  };

  const subtractQuantity = async () => {
    if (quantity) {
      const newQuantity = roundInSteps(quantity - caseMinimum, caseMinimum);
      await dispatch(
        updateProductOrders(orderId, {
          brandId,
          productId,
          originalQuantity,
          quantity: newQuantity,
          sku,
          type: "PRODUCT",
        }),
      );
    }
  };

  const handleOnChangeNumber = (nextQuantity: number | null) => {
    setQuantity(nextQuantity ?? 0);
  };

  const handleOnBlur = () => {
    if (rCanEdit) {
      const newQuantity = roundInSteps(Math.min(originalQuantity || quantity, sQuantity), caseMinimum);
      dispatch(
        updateProductOrders(orderId, {
          brandId,
          productId,
          originalQuantity,
          quantity: newQuantity,
          sku,
          type: "PRODUCT",
        }),
      );

      if (newQuantity !== sQuantity) setQuantity(newQuantity);
    }
  };

  useEffect(() => {
    setQuantity(quantity);
  }, [quantity]);

  return (
    <View style={styles.quantityCell}>
      {rCanEdit && (
        <TouchableOpacity style={[styles.alignCenter, styles.justifyCenter, styles.w20]} onPress={subtractQuantity}>
          <PdText style={styles.cartQuantityChangeButtonLabel}>-</PdText>
        </TouchableOpacity>
      )}
      <PdInputNumber
        style={styles.quantityInputNumber}
        maxLength={4}
        value={sQuantity}
        onChangeNumber={handleOnChangeNumber}
        onBlur={handleOnBlur}
        editable={rCanEdit}
      />
      {rCanEdit && (
        <TouchableOpacity style={[styles.alignCenter, styles.justifyCenter, styles.w20]} onPress={addQuantity}>
          <PdText style={styles.cartQuantityChangeButtonLabel}>+</PdText>
        </TouchableOpacity>
      )}
    </View>
  );
};

const RemoveCell: FC<
  Pick<tTableProduct, "productId" | "brandId" | "sku" | "originalQuantity"> & {
    orderId: string;
    style?: StyleProp<TextStyle>;
  }
> = ({ productId, brandId, sku, originalQuantity, orderId, style }) => {
  const dispatch = useAppDispatch();

  const handleOnPress = () => {
    dispatch(
      updateProductOrders(orderId, {
        brandId,
        productId,
        originalQuantity,
        quantity: 0,
        sku,
        type: "PRODUCT",
      }),
    );
  };

  return (
    <PdIconButton
      contentContainerStyle={styles.removeButton}
      onPress={handleOnPress}
      size="sm"
      variant="ghost"
      icon={<PdTrash style={styles.trashIcon} />}
      style={style}
    />
  );
};

const useTableColumnsProducts = (orderId: string, orderStatus: tOrderStatus) => {
  const { isBrand } = useUserProfile();
  return useMemo(() => {
    const columns: tColumn<tTableProduct>[] = [
      {
        id: "name",
        header: "Product",
        accessor: (originalRow) => originalRow.name,
        Cell: ({ value, row, style }) => (
          <View style={[styles.flexRow, styles.flexWrap, styles.alignCenter, style]}>
            <PdTableCellText numberOfLines={undefined} style={styles.maxW300}>
              {value}
            </PdTableCellText>
            {row.isTester && <TesterBadge style={styles.ml2} />}
          </View>
        ),
        containerStyle: { flex: 4 },
        style: { flex: 4 },
      },
      {
        id: "sku",
        header: "SKU",
        accessor: (originalRow) => originalRow.sku,
        Cell: ({ value }) => <PdTableCellText numberOfLines={undefined}>{value}</PdTableCellText>,
        containerStyle: { flex: 1 },
        style: { flex: 1 },
      },
      {
        id: "quantityControls",
        header: "Quantity",
        accessor: (originalRow) => originalRow.quantity,
        Cell: ({ row }) => (
          <QuantityControlOrder
            productId={row.productId}
            brandId={row.brandId}
            sku={row.sku}
            quantity={row.quantity}
            originalQuantity={row.originalQuantity}
            caseMinimum={row.caseMinimum}
            orderId={orderId}
            orderStatus={orderStatus}
          />
        ),
        isSortable: false,
        containerStyle: { flex: 2 },
        style: { flex: 2, justifyContent: "center" },
      },
      {
        id: "costPerUnit",
        header: "Per Unit",
        accessor: (originalRow) => originalRow.costPerUnit,
        Cell: ({ row }) => <PdTableCellText>{formatMoney(row.costPerUnit)}</PdTableCellText>,
        containerStyle: { flex: 1 },
        style: { flex: 1 },
      },
      {
        id: "total",
        header: "Total",
        accessor: (originalRow) => originalRow.total,
        Cell: ({ row }) => <PdTableCellText>{formatMoney(row.total)}</PdTableCellText>,
        containerStyle: { flex: 1 },
        style: { flex: 1 },
      },
    ];

    if (isBrand && orderStatus !== "Shipped") {
      columns.push({
        id: "remove-button",
        header: "Remove",
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row, style }) =>
          !!row.quantity && (
            <RemoveCell
              productId={row.productId}
              brandId={row.brandId}
              sku={row.sku}
              originalQuantity={row.originalQuantity}
              orderId={orderId}
              style={style}
            />
          ),
        isSortable: false,
        style: { width: 50, alignItems: "center", justifyContent: "center" },
      });
    }

    return columns;
  }, [isBrand, orderId, orderStatus]);
};

const ProductsTable: FC<{ order: tOrder }> = ({ order }) => {
  const navigation = useNavigation();
  const { isBrand } = useUserProfile();
  const columns = useTableColumnsProducts(order.orderId, order.orderStatus);
  const tableInstance = useTable<tTableProduct>({ data: order.cart.products.map(mapTableProduct), columns });
  const isMobile = useIsMobile();

  const handleOnPress = (item: tRowInstance<tTableProduct>) => {
    if (isBrand) {
      navigation.navigate(kRoute.P08_01, { id: item.productId });
    } else {
      navigation.navigate(kRoute.P101, { brand: item.brandName, productId: item.productId });
    }
  };

  const renderItem = ({ item }: { item: tRowInstance<tTableProduct> }) => {
    return (
      <Pressable onPress={() => handleOnPress(item)}>
        {({ pressed, hovered }: tPressableState) => (
          <PdTableRow
            style={[
              !isBrand && item.quantity === 0 ? styles.disabled : {},
              hovered ? styles.tableRowHovered : {},
              pressed ? styles.tableRowPressed : {},
            ]}
          >
            {item.cells.map((cell) => (
              <PdTableCell key={cell.uniqueId} style={[styles.heightAuto, cell.style]}>
                {cell.render("Cell")}
              </PdTableCell>
            ))}
          </PdTableRow>
        )}
      </Pressable>
    );
  };

  return (
    <ScrollView horizontal contentContainerStyle={isMobile ? {} : styles.flex1}>
      <FlatList
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        ListFooterComponent={<PdDivider style={styles.tableDivider} />}
        ListEmptyComponent={PdTableEmpty}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
      />
    </ScrollView>
  );
};

const Content: FC<{ order: tOrder }> = ({ order }) => {
  const {
    orderId,
    orderStatus,
    cart,
    connection,
    paymentMethod,
    dateCreated,
    orderMessage,
    shippingNumber,
    isSplit,
    trackingNumber,
    orderNumber,
    dateShipped,
  } = order;
  const dispatch = useAppDispatch();
  const kNav = useNavigation();
  const isMobile = useIsMobile();
  const rType = useSelector((state) => state.UserReducer.profile.accountType);
  const [sTrackingNumber, setTrackingNumber] = useState<string>("");
  const [sShipmentDate, setShipmentDate] = useState<Date>(new Date());
  const [sOrderComment, setOrderComment] = useState<string>("");
  const [sIsAddingShipping, setAddingShipping] = useState(false);
  const [sIsAcceptingOrder, setAcceptingOrder] = useState(false);
  const [sIsDecliningOrder, setDecliningOrder] = useState(false);
  const isFocused = useIsFocused();
  const alertModal = useModal<tAlertModalProps>(AlertModal);
  const invoiceMutation = useMutation(React.useCallback(() => getOrderInvoice(orderId), [orderId]));
  const packingSlipMutation = useMutation(React.useCallback(() => getOrderPackingSlip(orderId), [orderId]));
  const splitOrderModal = useSplitOrderModal();

  const repCommissionAmount = cart.totalPrice * (cart.rep?.repPercent ?? 0);
  const hasRep = cart.rep && cart.rep?.id && cart.rep?.vendorId;
  const marketplaceFeeAmount = cart.commissionFee ?? 0;
  const serviceFeeAmount = cart.serviceFee ?? 0;
  const creditUsed = (cart.creditUsedAmount ?? 0) + (cart.marketplaceCreditUsedAmount ?? 0);
  const retailerDefaultAddress = getDefaultAddress(connection.boutique.addresses);
  const shippingAddress = cart.address;
  const rIsAdmin = rType === "tmtAdmin";
  const rIsBrand = rType === "brand";
  const rIsRetailer = rType === "retailer";
  const rIsRep = rType === "rep";
  const rAccepted = orderStatus !== "Pending";
  const rCanAccept = (rIsBrand || rIsAdmin) && orderStatus === "Pending";
  const rCanDecline = (rIsBrand || rIsAdmin) && (orderStatus === "Pending" || orderStatus === "Awaiting Fulfillment");
  const rCanEditComment = (rIsBrand || rIsAdmin) && orderStatus !== "Shipped";
  const rCanShipOrSave = (rIsBrand || rIsAdmin) && orderStatus !== "Pending";
  const rCanDisplayCommissions = rIsAdmin || rIsBrand;
  const rCanDisplayRepCommission = hasRep && !rIsRep && !rIsRetailer;

  const total = formatMoney(
    cart.totalPrice +
      cart.totalPriceWithShipping -
      (rCanDisplayRepCommission ? repCommissionAmount : 0) -
      (rCanDisplayCommissions ? marketplaceFeeAmount : 0) -
      (rCanDisplayCommissions ? serviceFeeAmount : 0),
  );

  const handleNavigateToShoppingBrand = () => {
    kNav.navigate(kRoute.P100, { brand: connection.brand.name });
  };

  const handleOnPressSplitOrder = () => {
    splitOrderModal.openAsync({ orderId, products: cart.products });
  };

  const handleOnPrintInvoice = async () => {
    try {
      const url = await invoiceMutation.mutateAsync();
      openUrlInNewTab(url);
    } catch (error) {
      const apiError = getApiError(error);
      dispatch({ type: "RIBBON", message: new Error(apiError.message) });
    }
  };

  const handleOnPrintPackingSlip = async () => {
    try {
      const url = await packingSlipMutation.mutateAsync();
      openUrlInNewTab(url);
    } catch (error) {
      const apiError = getApiError(error);
      dispatch({ type: "RIBBON", message: new Error(apiError.message) });
    }
  };

  const _acceptOrder = async () => {
    setAcceptingOrder(true);
    await dispatch(updateOrder(orderId, { comment: sOrderComment, type: "COMMENT", brandId: connection.brand.id }));
    setAcceptingOrder(false);
  };

  const _declineOrder = async () => {
    setDecliningOrder(true);
    await dispatch(declineOrder(orderId, { type: "DECLINE_ORDER", brandId: connection.brand.id }));
    setDecliningOrder(false);
  };

  const _setOrderCommentState = useCallback((myText: string) => setOrderComment(myText), []);

  const _handleDatePick = useCallback((myDate) => {
    if (myDate === null) return;
    setShipmentDate(myDate);
  }, []);

  const _addShipping = async () => {
    if (rCanShipOrSave) {
      setAddingShipping(true);
      await dispatch(
        addShipping(orderId, {
          type: "SHIPPING",
          shippingDate: sShipmentDate?.toISOString() ?? "",
          trackingNumber: sTrackingNumber,
          brandId: connection.brand.id,
          comment: sOrderComment,
        }),
      );
      setAddingShipping(false);
    }
  };

  const handleReorder = async () => {
    try {
      const response = await postReorder(orderId);
      if (response?.message) {
        alertModal.openAsync({ title: "INVENTORY ALERT", description: response.message });
      } else {
        dispatch({ type: "RIBBON", message: "Products added to cart" });
      }
    } catch (error) {
      if (isApiError(error) && error?.response?.data.status === 409) {
        const response = await alertModal.openAsync({
          title: "INVENTORY ALERT",
          description: error.response.data.message,
          buttonLabel: "Shop Brand",
        });
        if (response.status === "closed") return;
        handleNavigateToShoppingBrand();
      } else {
        dispatch({ type: "RIBBON", message: "An error occurred please try again." });
      }
    }
  };

  React.useEffect(() => {
    if (isFocused) {
      setTrackingNumber((shippingNumber || trackingNumber) ?? "");
      setShipmentDate(new Date((dateShipped?.seconds ?? Date.now() / 1000) * 1000));
      setOrderComment(orderMessage || "");
    }
  }, [dateShipped?.seconds, isFocused, orderMessage, shippingNumber, trackingNumber]);

  return (
    <ScrollView
      contentContainerStyle={[styles.bgWhite, isMobile ? [styles.mx5, styles.my3] : [styles.mx10, styles.my5]]}
    >
      <PdStack direction="horizontal" style={styles.mb5}>
        {rIsBrand && orderStatus === "Pending" && (
          <PdButton
            leftIcon={<PdPapers color={kColors.brandGreen} style={styles.buttonLeftIcon} />}
            onPress={handleOnPressSplitOrder}
            variant="ghost"
            contentContainerStyle={styles.px0}
          >
            <PdButtonLabel>Split Order</PdButtonLabel>
          </PdButton>
        )}
        {!!isSplit && (
          <PdButton
            leftIcon={<PdCheck color={kColors.brandGreen} style={styles.checkIcon} />}
            variant="ghost"
            contentContainerStyle={styles.px0}
            isDisabled
          >
            <PdButtonLabel>Order Split!</PdButtonLabel>
          </PdButton>
        )}
        {orderStatus === "Shipped" && (
          <PdButton
            isLoading={invoiceMutation.isLoading}
            leftIcon={<PdPaper color={kColors.brandGreen} style={styles.buttonLeftIcon} />}
            onPress={handleOnPrintInvoice}
            variant="ghost"
            contentContainerStyle={styles.px0}
          >
            <PdButtonLabel>Invoice</PdButtonLabel>
            <PdArrowUpRight style={[styles.buttonRightIcon, styles.ml1]} />
          </PdButton>
        )}
        {rIsBrand && (
          <PdButton
            isLoading={packingSlipMutation.isLoading}
            leftIcon={<PdPaperAlt style={styles.buttonLeftIcon} />}
            onPress={handleOnPrintPackingSlip}
            variant="ghost"
            contentContainerStyle={styles.px0}
            style={styles.mlAuto}
          >
            <PdButtonLabel>Packing Slip</PdButtonLabel>
            <PdArrowUpRight style={[styles.buttonRightIcon, styles.ml1]} />
          </PdButton>
        )}
        {rIsRetailer && (
          <PdButton
            leftIcon={<PdReorder style={styles.reorderLeftIcon} />}
            colorScheme="purple"
            onPress={handleReorder}
            variant="ghost"
            contentContainerStyle={styles.px0}
          >
            <PdButtonLabel style={styles.colorBrandPurple}>Reorder</PdButtonLabel>
          </PdButton>
        )}
      </PdStack>
      <PdStack
        layout={[1, 1]}
        direction={isMobile ? "vertical" : "horizontal"}
        style={[isMobile ? {} : styles.alignEnd, styles.mb2]}
      >
        <View>
          <View style={[styles.dlStart, styles.mb3]}>
            <Text style={styles.dt}>Order #</Text>
            <Text style={styles.dd}>{orderNumber}</Text>
          </View>
          {!!cart.PONumber && (
            <View style={[styles.dlStart, styles.mb3]}>
              <Text style={styles.dt}>PO #</Text>
              <Text style={styles.dd}>{cart.PONumber}</Text>
            </View>
          )}
          <View style={styles.P03_01OrderTitleLeftSubView}>
            <Text style={styles.orderName}>{connection.boutique.name}</Text>
            {orderStatus && (
              <PdBadge style={[styles.ml2, { backgroundColor: getThemeColorFromOrderStatus(orderStatus) }]}>
                <PdBadgeLabel>{orderStatus}</PdBadgeLabel>
              </PdBadge>
            )}
          </View>
        </View>
        <View style={isMobile ? styles.alignStart : styles.alignEnd}>
          <View style={[isMobile ? styles.dlStart : styles.dlEnd, styles.mb3]}>
            <Text style={styles.dt}>Order Date</Text>
            <Text style={styles.dd}>{dateCreated?.seconds ? formatDate(dateCreated.seconds * 1000) : ""}</Text>
          </View>
          <View style={[isMobile ? styles.dlStart : styles.dlEnd, styles.mb3]}>
            <Text style={styles.dt}>Order Source</Text>
            <Text style={styles.dd}>{hasRep ? "Sales Rep" : displayProperConnectionSource(cart.connectionSource)}</Text>
          </View>
          {hasRep && (
            <View style={[isMobile ? styles.dlStart : styles.dlEnd, styles.mb3]}>
              <Text style={styles.dt}>Sales Rep</Text>
              <Text style={styles.dd}>{cart.rep?.name ?? "-"}</Text>
            </View>
          )}
          <View style={isMobile ? styles.dlStart : styles.dlEnd}>
            <Text style={styles.dt}>Payment Terms</Text>
            <Text style={styles.dd}>{formatPaymentMethod(paymentMethod)}</Text>
          </View>
        </View>
      </PdStack>
      <View style={[styles.divider, styles.my2]} />
      <PdStack layout={[1, 1]} direction={isMobile ? "vertical" : "horizontal"} style={styles.mb8}>
        <View>
          <Text style={styles.addressType}>Primary Address</Text>
          {!!retailerDefaultAddress.name && <Text style={styles.headerText}>{retailerDefaultAddress.name}</Text>}
          <Text style={styles.headerText}>{retailerDefaultAddress.address1}</Text>
          {!!retailerDefaultAddress.address2 && (
            <Text style={styles.headerText}>{retailerDefaultAddress.address2}</Text>
          )}
          <Text style={styles.headerText}>{formatShortAddress(retailerDefaultAddress)}</Text>
          {!!retailerDefaultAddress.phone && !connection.boutique.phone && (
            <Text style={styles.headerText}>{formatPhoneNumber(retailerDefaultAddress.phone)}</Text>
          )}
          {!retailerDefaultAddress.phone && !!connection.boutique.phone && (
            <Text style={styles.headerText}>{formatPhoneNumber(connection.boutique.phone)}</Text>
          )}
        </View>
        <View style={isMobile ? styles.alignStart : styles.alignEnd}>
          <Text style={styles.addressType}>{orderStatus === "Shipped" ? "Shipped to" : "Ship to"}</Text>
          {!!shippingAddress.name && <Text style={styles.infoText}>{shippingAddress.name}</Text>}
          <Text style={styles.infoText}>{shippingAddress.address1}</Text>
          {!!shippingAddress.address2 && <Text style={styles.infoText}>{shippingAddress.address2}</Text>}
          <Text style={styles.infoText}>{formatShortAddress(shippingAddress)}</Text>
          {!!shippingAddress.phone && <Text style={styles.infoText}>{formatPhoneNumber(shippingAddress.phone)}</Text>}
        </View>
      </PdStack>
      <TouchableOpacity style={styles.mb5} onPress={handleNavigateToShoppingBrand}>
        <Text style={styles.headerSecondaryText}>{connection.brand.name}</Text>
      </TouchableOpacity>
      <ProductsTable order={order} />
      <View style={isMobile ? styles.my2 : [styles.flexRow, styles.flexWrap, styles.my5]}>
        {rAccepted && orderStatus !== "Declined" && (
          <View style={isMobile ? styles.mb3 : styles.mr4}>
            <Text style={styles.dt}>Details</Text>
            <View style={[styles.divider, styles.mb3]} />
            {rCanShipOrSave ? (
              <>
                <View>
                  <PdFormLabel style={styles.mb1}>Tracking Numbers</PdFormLabel>
                  <PdInput onChangeText={setTrackingNumber} value={sTrackingNumber} style={styles.widthFull} />
                  <PdText style={[styles.helpText, styles.mb2]}>Separate tracking numbers by comma</PdText>
                </View>
                <View>
                  <PdFormLabel style={styles.mb1}>Shipped Date</PdFormLabel>
                  <PdDateInput selected={sShipmentDate} onChange={_handleDatePick} style={styles.widthFull} />
                </View>
              </>
            ) : (
              <>
                <View style={styles.detailsRow}>
                  <Text style={[styles.infoSecondaryText, styles.wSm]}>Tracking Numbers</Text>
                  <Text style={[styles.infoText, styles.textRight, styles.flex1]}>{sTrackingNumber}</Text>
                </View>
                <View style={styles.detailsRow}>
                  <Text style={[styles.infoSecondaryText, styles.wSm]}>Shipped Date</Text>
                  <Text style={[styles.infoText, styles.textRight, styles.flex1]}>
                    {formatDate(sShipmentDate.getTime())}
                  </Text>
                </View>
              </>
            )}
          </View>
        )}

        <View style={[styles.mlAuto, styles.zIndexLess1]}>
          <View style={styles.detailsRow}>
            <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>Subtotal</Text>
            <Text style={[styles.infoText, styles.flex1, styles.textRight]}>
              {formatMoney(cart.totalPrice + (cart.discountCost ?? 0))}
            </Text>
          </View>
          <View style={styles.detailsRow}>
            <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>
              Shipping and Handling
            </Text>
            <Text style={[styles.infoText, styles.flex1, styles.textRight]}>
              {cart.shippingNumber || formatMoney(cart.totalPriceWithShipping)}
            </Text>
          </View>
          {!!cart.discountCost && (
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>Discount</Text>
              <Text style={[styles.infoText, styles.flex1, styles.textRight]}>-{formatMoney(cart.discountCost)}</Text>
            </View>
          )}
          {rCanDisplayCommissions && cart.connectionSource === "TMT" && !hasRep && (
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>
                Calculated Commission
              </Text>
              <Text style={[styles.infoText, styles.flex1, styles.textRight, { color: kColors.gray500 }]}>
                {formatMoney(marketplaceFeeAmount)}
              </Text>
            </View>
          )}
          {rCanDisplayCommissions && (
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>Service Fee</Text>
              <Text style={[styles.infoText, styles.flex1, styles.textRight, { color: kColors.gray500 }]}>
                {formatMoney(serviceFeeAmount)}
              </Text>
            </View>
          )}
          {rCanDisplayRepCommission && (
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>
                Sales Rep Commission
              </Text>
              <Text style={[styles.infoText, styles.flex1, styles.textRight, { color: kColors.gray500 }]}>
                {formatMoney(repCommissionAmount)}
              </Text>
            </View>
          )}
          <View style={[styles.divider, styles.my2]} />
          <View style={styles.detailsRow}>
            <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>
              {rIsBrand ? "Total Payout" : "Total"}
            </Text>
            <Text style={[styles.infoText, styles.flex1, styles.textRight]}>{total}</Text>
          </View>
          {rIsRep && (
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>
                Commission Earned
              </Text>
              <Text style={[styles.infoText, styles.flex1, styles.textRight]}>{formatMoney(repCommissionAmount)}</Text>
            </View>
          )}
          {(rIsAdmin || rIsRetailer) && !!creditUsed && (
            <View style={styles.detailsRow}>
              <Text style={[styles.infoSecondaryText, styles.mr4, styles.wMd, styles.textRight]}>
                Main Tab Credit redeemed
              </Text>
              <Text style={[styles.infoText, styles.flex1, styles.textRight, { color: kColors.gray500 }]}>
                {formatMoney(creditUsed)}
              </Text>
            </View>
          )}
        </View>
      </View>
      <View style={[styles.gLinkBankFormItemNoMargin, styles.zIndexLess1]}>
        <View style={styles.P03_01OrderTitleLeftSubView}>
          <Text style={styles.dt}>Comment</Text>
        </View>
        <TextInput
          style={styles.textInput}
          placeholder="Inform your Retailer about any changes you make here. Once you accept the order, you will not be able to add or edit any additional notes here."
          placeholderTextColor={kColors.mediumGrey}
          multiline
          numberOfLines={5}
          value={sOrderComment}
          onChangeText={_setOrderCommentState}
          editable={rCanEditComment}
        />
      </View>
      {(rCanDecline || rCanShipOrSave || rCanAccept) && (
        <PdStack
          direction={isMobile ? "vertical" : "horizontal"}
          style={[styles.justifyEnd, isMobile ? styles.flexReverse : {}]}
        >
          {rCanDecline && (
            <PdButton
              isDisabled={sIsAcceptingOrder}
              isLoading={sIsDecliningOrder}
              variant="outline"
              onPress={_declineOrder}
              style={styles.minWidthMd}
            >
              <PdButtonLabel>Decline</PdButtonLabel>
            </PdButton>
          )}
          {rCanShipOrSave && (
            <PdButton isLoading={sIsAddingShipping} onPress={_addShipping} style={styles.minWidthMd}>
              <PdButtonLabel>
                {orderStatus === "Awaiting Fulfillment" ? "Ship & Complete Order!" : "Save"}
              </PdButtonLabel>
            </PdButton>
          )}
          {rCanAccept && (
            <PdButton
              isDisabled={sIsDecliningOrder}
              isLoading={sIsAcceptingOrder}
              onPress={_acceptOrder}
              style={[styles.minWidthMd, isMobile ? styles.mb2 : {}]}
            >
              <PdButtonLabel>Accept</PdButtonLabel>
            </PdButton>
          )}
        </PdStack>
      )}
    </ScrollView>
  );
};

const P03_01_ViewOrder: FC<{
  route: RouteProp<tpViewOrderParamList, "order">;
  navigation: NavigationProp<ParamListBase>;
}> = ({ route }) => {
  const isFocused = useIsFocused();
  const assignRepOrderModal = useAssignRepOrderModal();
  const [order, setOrder] = React.useState<tOrder | undefined>(undefined);

  React.useEffect(() => {
    const unsubscribe = orderDb.documentOrder(route.params?.id).onSnapshot((document) => {
      setOrder(document.data());
    });

    return () => {
      unsubscribe();
    };
  }, [route.params?.id, setOrder]);

  React.useEffect(() => {
    if (
      order &&
      !order.didBrandConfirmRepAssignment &&
      order.cart.draftReferralCompanyId &&
      order.cart.draftReferralCompanyId !== order.cart.rep?.id
    ) {
      assignRepOrderModal.open({
        orderId: order.orderId,
        orderTotal: order.cart.totalPrice,
        repIdToAssign: order.cart.draftReferralCompanyId,
        isRepAssigned: !!order.cart.rep?.id,
      });
    }
  }, [assignRepOrderModal, order]);

  React.useEffect(() => {
    if (!isFocused) {
      setOrder(undefined);
    }
  }, [isFocused]);

  if (
    !order ||
    (order &&
      !order.didBrandConfirmRepAssignment &&
      order.cart.draftReferralCompanyId &&
      order.cart.draftReferralCompanyId !== order.cart.rep?.id)
  ) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return <Content order={order} />;
};

export const P03_01 = React.memo(P03_01_ViewOrder);

const styles = StyleSheet.create({
  P03_01OrderTitleLeftSubView: {
    alignItems: "center",
    flexDirection: "row",
  },
  addressType: {
    ...kFonts.poppins400,
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  alignStart: {
    alignItems: "flex-start",
  },
  bgWhite: {
    backgroundColor: kColors.white,
  },
  buttonLeftIcon: {
    height: 20,
    width: 21,
  },
  buttonRightIcon: {
    height: 7,
    width: 7,
  },
  cartQuantityChangeButtonLabel: {
    fontSize: 22,
    fontWeight: "100",
    textAlign: "center",
  },
  checkIcon: {
    height: 18,
    width: 18,
  },
  colorBrandPurple: {
    color: kColors.brandPurple,
  },
  dd: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontWeight: "500",
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  detailsRow: {
    flexDirection: "row",
  },
  disabled: {
    opacity: 0.4,
  },
  divider: {
    backgroundColor: kColors.gray300,
    height: 0.75,
    width: "100%",
  },
  dlEnd: {
    alignItems: "flex-end",
  },
  dlStart: {
    alignItems: "flex-start",
  },
  dt: {
    color: kColors.brandDark300,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1,
    lineHeight: 18,
    textTransform: "uppercase",
  },
  flex1: {
    flex: 1,
  },
  flexReverse: {
    flexDirection: "column-reverse",
  },
  flexRow: {
    flexDirection: "row",
  },
  flexWrap: {
    flexWrap: "wrap",
  },
  gLinkBankFormItemNoMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  headerSecondaryText: {
    fontFamily: "Raleway",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 1,
    lineHeight: 30,
    textTransform: "uppercase",
  },
  headerText: {
    ...kFonts.poppins400,
    color: kColors.gray500,
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  heightAuto: {
    height: "100%",
  },
  helpText: {
    ...kFonts.poppinsLightSmall,
    color: kColors.gray500,
    letterSpacing: 1.1,
    lineHeight: 18,
    minWidth: 200,
  },
  infoSecondaryText: {
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1,
    lineHeight: 30,
    textTransform: "uppercase",
  },
  infoText: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: "400",
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  justifyCenter: {
    justifyContent: "center",
  },
  justifyEnd: {
    justifyContent: "flex-end",
  },
  maxW300: {
    maxWidth: 300,
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb2: {
    marginBottom: 2 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  mb8: {
    marginBottom: 8 * kStyleConst.kSpacing,
  },
  minWidthMd: {
    minWidth: 191,
  },
  ml1: {
    marginLeft: kStyleConst.kSpacing,
  },
  ml2: {
    marginLeft: 2 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mr4: {
    marginRight: 4 * kStyleConst.kSpacing,
  },
  mx10: {
    marginHorizontal: 10 * kStyleConst.kSpacing,
  },
  mx5: {
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
  my3: {
    marginVertical: 3 * kStyleConst.kSpacing,
  },
  my5: {
    marginVertical: 5 * kStyleConst.kSpacing,
  },
  orderName: {
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 22,
    fontWeight: "500",
    letterSpacing: 1.1,
  },
  px0: {
    paddingHorizontal: 0,
  },
  quantityCell: {
    alignItems: "center",
    flexDirection: "row",
    textAlign: "center",
  },
  quantityInputNumber: {
    borderWidth: 0,
    textAlign: "center",
    width: 60,
  },
  removeButton: {
    borderRadius: 999,
    height: 34,
    marginVertical: -kStyleConst.kSpacing * 2,
    width: 34,
  },
  reorderLeftIcon: {
    height: 20,
    width: 25,
  },
  tableDivider: {
    marginVertical: kStyleConst.kSpacing * 2,
  },
  tableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  tableRowPressed: {
    opacity: 0.6,
  },
  textInput: {
    borderColor: kColors.gray300,
    borderWidth: 1,
    fontFamily: "Poppins",
    fontWeight: "400",
    letterSpacing: 1.1,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    padding: 2 * kStyleConst.kSpacing,
  },
  textRight: {
    textAlign: "right",
  },
  trashIcon: {
    width: 14,
  },
  w20: {
    width: 20,
  },
  wMd: {
    width: 200,
  },
  wSm: {
    width: 130,
  },
  widthFull: {
    width: "100%",
  },
  zIndexLess1: {
    zIndex: -1,
  },
});
